import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const MAX_EMAIL_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export const getEmailAttachmentSendMode = ({ usesStoredTemplateAttachments = false } = {}) => (
    usesStoredTemplateAttachments ? 'server' : 'copy'
);

const MAX_FILENAME_LENGTH = 255;

const safeDecodeURIComponent = (value) => {
    try {
        return decodeURIComponent(value);
    } catch (error) {
        return value;
    }
};

export const getAttachmentFilename = (attachment = {}) => {
    const suppliedName = attachment.original_filename || attachment.name;
    let candidate = suppliedName;
    if (!candidate && attachment.file && !String(attachment.file).startsWith('data:')) {
        const withoutQuery = String(attachment.file).split(/[?#]/)[0];
        candidate = safeDecodeURIComponent(withoutQuery);
    }
    const basename = String(candidate || 'attachment').split(/[\\/]/).pop()
        .split('')
        .filter((character) => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127)
        .join('')
        .trim() || 'attachment';
    return basename.length > MAX_FILENAME_LENGTH ? basename.slice(-MAX_FILENAME_LENGTH) : basename;
};

export const validateAttachmentFiles = (files = []) => {
    const oversized = files.find((file) => Number(file?.size) > MAX_EMAIL_ATTACHMENT_BYTES);
    if (!oversized) return null;
    return {
        filename: getAttachmentFilename(oversized),
        message: `${getAttachmentFilename(oversized)} exceeds the 10 MB attachment limit.`,
        type: 'size',
    };
};

export const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    if (!file) {
        reject(new Error('Attachment file is missing.'));
        return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Unable to read ${getAttachmentFilename(file)}.`));
    reader.onabort = () => reject(new Error(`Reading ${getAttachmentFilename(file)} was cancelled.`));
    reader.readAsDataURL(file);
});

export const fetchAttachmentAsDataUrl = async (url) => {
    if (!url || typeof url !== 'string') {
        throw new Error('Saved attachment is missing a file URL.');
    }
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Unable to load saved attachment (${response.status}).`);
    }
    return readFileAsDataUrl(await response.blob());
};

const retainedAttachment = (attachment) => ({
    id: attachment.id,
    file: attachment.file,
    original_filename: getAttachmentFilename(attachment),
});

const copiedAttachment = async (attachment, fetchAsDataUrl) => {
    if (!attachment?.file || typeof attachment.file !== 'string') {
        throw new Error(`${getAttachmentFilename(attachment)} is missing a file URL.`);
    }
    return {
        file: await fetchAsDataUrl(attachment.file),
        original_filename: getAttachmentFilename(attachment),
    };
};

export const createEmailAttachmentController = ({
    readFileAsDataUrl: readLocalFile = readFileAsDataUrl,
    fetchAsDataUrl = fetchAttachmentAsDataUrl,
} = {}) => {
    let latestOperation = 0;

    return {
        cancel() {
            latestOperation += 1;
        },
        async prepare({ savedAttachments = [], newFiles = [], mode = 'retain' } = {}) {
            const operation = latestOperation + 1;
            latestOperation = operation;
            try {
                const validationError = validateAttachmentFiles(newFiles);
                if (validationError) {
                    const error = new Error(validationError.message);
                    error.attachmentValidation = validationError;
                    throw error;
                }

                const savedPayload = await Promise.all(savedAttachments.map((attachment) => {
                    if (mode === 'retain' && attachment?.id) return retainedAttachment(attachment);
                    return copiedAttachment(attachment, fetchAsDataUrl);
                }));
                const newPayload = await Promise.all(newFiles.map(async (file) => ({
                    file: await readLocalFile(file),
                    original_filename: getAttachmentFilename(file),
                })));

                return {
                    attachments: [...savedPayload, ...newPayload],
                    stale: operation !== latestOperation,
                };
            } catch (error) {
                error.stale = operation !== latestOperation;
                throw error;
            }
        },
    };
};

const attachmentSignature = (attachments) => JSON.stringify((attachments || []).map((attachment) => [
    attachment?.id || null,
    attachment?.file || null,
    attachment?.original_filename || null,
]));

const initialStatus = {
    dirty: false,
    error: null,
    pending: false,
    templatePayload: [],
    validationError: null,
};

export default function useEmailAttachments({ savedAttachments = [], resetKey = '' } = {}) {
    const controllerRef = useRef(createEmailAttachmentController());
    const revisionRef = useRef(0);
    const resetKeyRef = useRef(resetKey);
    const savedRef = useRef(savedAttachments || []);
    const newFilesRef = useRef([]);
    const [saved, setSaved] = useState(savedAttachments || []);
    const [newFiles, setNewFiles] = useState([]);
    const [status, setStatus] = useState(initialStatus);
    const signature = useMemo(() => attachmentSignature(savedAttachments), [savedAttachments]);
    resetKeyRef.current = resetKey;

    const reset = useCallback((nextSaved = []) => {
        controllerRef.current.cancel();
        revisionRef.current += 1;
        savedRef.current = nextSaved || [];
        newFilesRef.current = [];
        setSaved(nextSaved || []);
        setNewFiles([]);
        setStatus(initialStatus);
    }, []);

    useEffect(() => {
        reset(savedAttachments);
    // The signature changes only when the authoritative attachment list changes;
    // body/editor-mode updates do not clear pending local files.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey, signature, reset]);

    useEffect(() => () => controllerRef.current.cancel(), []);

    const prepareTemplatePayload = useCallback(async (nextSaved, nextFiles) => {
        const validationError = validateAttachmentFiles(nextFiles);
        if (validationError) {
            controllerRef.current.cancel();
            setStatus((current) => ({
                ...current,
                dirty: true,
                error: null,
                pending: false,
                validationError,
            }));
            return null;
        }

        setStatus((current) => ({
            ...current,
            dirty: true,
            error: null,
            pending: true,
            validationError: null,
        }));
        try {
            const result = await controllerRef.current.prepare({
                mode: 'retain',
                newFiles: nextFiles,
                savedAttachments: nextSaved,
            });
            if (result.stale) return null;
            setStatus({
                dirty: true,
                error: null,
                pending: false,
                templatePayload: result.attachments,
                validationError: null,
            });
            return result.attachments;
        } catch (error) {
            if (error.stale) return null;
            setStatus((current) => ({
                ...current,
                dirty: true,
                error,
                pending: false,
                validationError: error.attachmentValidation || null,
            }));
            return null;
        }
    }, []);

    const replaceNewFiles = useCallback((files = []) => {
        const nextFiles = Array.from(files || []);
        revisionRef.current += 1;
        newFilesRef.current = nextFiles;
        setNewFiles(nextFiles);
        return prepareTemplatePayload(savedRef.current, nextFiles);
    }, [prepareTemplatePayload]);

    const removeSavedAttachment = useCallback((attachmentOrUrl, currentNewFiles) => {
        const nextSaved = savedRef.current.filter((attachment) => (
            attachment !== attachmentOrUrl
            && attachment?.id !== attachmentOrUrl
            && attachment?.file !== attachmentOrUrl
        ));
        const nextFiles = currentNewFiles === undefined
            ? newFilesRef.current
            : Array.from(currentNewFiles || []);
        revisionRef.current += 1;
        savedRef.current = nextSaved;
        newFilesRef.current = nextFiles;
        setSaved(nextSaved);
        setNewFiles(nextFiles);
        return prepareTemplatePayload(nextSaved, nextFiles);
    }, [prepareTemplatePayload]);

    const prepareForSend = useCallback(async () => {
        const validationError = validateAttachmentFiles(newFilesRef.current);
        if (validationError) {
            setStatus((current) => ({ ...current, validationError }));
            return null;
        }
        setStatus((current) => ({
            ...current,
            error: null,
            pending: true,
            validationError: null,
        }));
        try {
            const result = await controllerRef.current.prepare({
                mode: 'copy',
                newFiles: newFilesRef.current,
                savedAttachments: savedRef.current,
            });
            if (result.stale) return null;
            setStatus((current) => ({ ...current, error: null, pending: false }));
            return result.attachments;
        } catch (error) {
            if (error.stale) return null;
            setStatus((current) => ({
                ...current,
                error,
                pending: false,
                validationError: error.attachmentValidation || null,
            }));
            return null;
        }
    }, []);

    const createSaveSnapshot = useCallback(() => ({
        resetKey: resetKeyRef.current,
        revision: revisionRef.current,
    }), []);

    const acknowledgeSaved = useCallback((snapshot, nextSaved = []) => {
        if (!snapshot
            || snapshot.resetKey !== resetKeyRef.current
            || snapshot.revision !== revisionRef.current) {
            return false;
        }
        reset(nextSaved);
        return true;
    }, [reset]);

    return {
        ...status,
        acknowledgeSaved,
        createSaveSnapshot,
        hasBlockingError: Boolean(status.error || status.validationError),
        newFiles,
        prepareForSend,
        removeSavedAttachment,
        replaceNewFiles,
        reset,
        savedAttachments: saved,
    };
}
